import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Home, MapPin, DollarSign, User, AlertCircle, Edit, Trash2, Shield } from 'lucide-react';
import { clientAPI } from '../services/api';
import { getPropertiesByClient, createProperty as createPropertyAPI, updateProperty as updatePropertyAPI } from '../services/propertyApi';
import propertyAPI from '../services/propertyApi';
import PhoneInput from '../components/PhoneInput';
import DocumentInput from '../components/DocumentInput';
import { useI18n } from '../contexts/I18nContext';
import AnimalManagementModal from '../components/AnimalManagementModal';
import SpecialInstructionsModal from '../components/SpecialInstructionsModal';
import Pagination from '../components/Pagination';

interface Property {
  id: string;
  client_id: string;
  client_name?: string;
  name: string;
  address: string;
  zip_code?: string;
  city?: string;
  state?: string;
  type: string;
  price: number;
  created_at: string;
}

interface Client {
  id: string;
  name: string;
  phone: string;
  document: string;
}

interface PropertyFormData {
  client_id: string;
  name: string;
  address: string;
  zip_code: string;
  city: string;
  state: string;
  type: string;
  price: string;
}

interface NewClientData {
  name: string;
  phone: string;
  document: string;
}

interface PropriedadesProps {
  selectedClient?: { id: string; name: string } | null;
  onClientCleared?: () => void;
}

const Propriedades: React.FC<PropriedadesProps> = ({ selectedClient, onClientCleared }) => {
  const { t } = useI18n();
  const [properties, setProperties] = useState<Property[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [newProperty, setNewProperty] = useState<PropertyFormData>({
    client_id: '',
    name: '',
    address: '',
    zip_code: '',
    city: '',
    state: '',
    type: 'house',
    price: ''
  });
  const [newClient, setNewClient] = useState<NewClientData>({
    name: '',
    phone: '',
    document: ''
  });
  const [error, setError] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<'BR' | 'US' | null>(null);
  const [showAnimalModal, setShowAnimalModal] = useState(false);
  const [selectedPropertyForAnimals, setSelectedPropertyForAnimals] = useState<{ id: string; name: string } | null>(null);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [selectedPropertyForInstructions, setSelectedPropertyForInstructions] = useState<{ id: string; name: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Função para detectar se é CEP brasileiro ou ZIP Code americano
  const detectPostalCodeType = (code: string): 'BR' | 'US' | null => {
    const cleanCode = code.replace(/\D/g, '');
    if (cleanCode.length === 8) return 'BR'; // CEP brasileiro: 8 dígitos
    if (cleanCode.length === 5 || cleanCode.length === 9) return 'US'; // ZIP Code: 5 ou 9 dígitos (ZIP+4)
    return null;
  };

  // Função para buscar CEP brasileiro via ViaCEP
  const fetchBrazilianCep = async (cep: string) => {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    
    if (data.erro) {
      throw new Error('CEP não encontrado');
    }
    
    return {
      zip_code: cep,
      city: data.localidade,
      state: data.uf,
      address: data.logradouro ? `${data.logradouro}, ${data.bairro}` : data.bairro || ''
    };
  };

  // Função para buscar ZIP Code americano via Zippopotam.us
  const fetchAmericanZipCode = async (zipCode: string) => {
    const cleanZip = zipCode.substring(0, 5); // Usar apenas os 5 primeiros dígitos
    const response = await fetch(`https://api.zippopotam.us/us/${cleanZip}`);
    
    if (!response.ok) {
      throw new Error('ZIP Code not found');
    }
    
    const data = await response.json();
    
    if (!data.places || data.places.length === 0) {
      throw new Error('ZIP Code not found');
    }
    
    const place = data.places[0];
    return {
      zip_code: zipCode,
      city: place['place name'],
      state: place['state abbreviation'],
      address: '' // API não retorna endereço específico
    };
  };

  // Função unificada para buscar CEP/ZIP Code
  const fetchAddressByCep = async (code: string) => {
    const cleanCode = code.replace(/\D/g, '');
    const codeType = detectPostalCodeType(cleanCode);
    
    if (!codeType) return;

    setLoadingCep(true);
    setDetectedCountry(codeType);
    
    try {
      let result;
      
      if (codeType === 'BR') {
        result = await fetchBrazilianCep(cleanCode);
      } else {
        result = await fetchAmericanZipCode(cleanCode);
      }
      
      setNewProperty({
        ...newProperty,
        ...result
      });
      
    } catch (error: any) {
      console.error('Erro ao buscar CEP/ZIP Code:', error);
      alert(error.message || 'Erro ao buscar CEP/ZIP Code');
      setDetectedCountry(null);
    } finally {
      setLoadingCep(false);
    }
  };

  // Função para mapear tipo do backend para tipo do frontend
  const mapPropertyTypeFromBackend = (backendType: string): string => {
    const typeMap: Record<string, string> = {
      'residential': 'house', // Mapeia residential para house (padrão do frontend)
      'commercial': 'commercial',
      'other': 'other'
    };
    return typeMap[backendType] || 'house';
  };

  const loadProperties = async () => {
    setIsLoading(true);
    try {
      let propertiesData: any[] = [];
      if (selectedClient?.id) {
        // Buscar propriedades do cliente selecionado
        const data = await getPropertiesByClient(selectedClient.id);
        propertiesData = Array.isArray(data) ? data : [];
      } else {
        // Buscar todas as propriedades usando o endpoint GetAllProperties
        const response = await propertyAPI.get('/properties');
        propertiesData = Array.isArray(response.data?.properties) ? response.data.properties : [];
      }
      
      // Garantir que propertiesData é um array antes de mapear
      if (!Array.isArray(propertiesData)) {
        console.warn('propertiesData não é um array:', propertiesData);
        propertiesData = [];
      }
      
      // Mapear property_type do backend para type do frontend
      const mappedProperties = propertiesData.map((p: any) => {
        const mapped = {
          ...p,
          type: p.property_type ? mapPropertyTypeFromBackend(p.property_type) : (p.type || 'house'),
          price: p.price || 0 // Garantir que price sempre existe
        };
        return mapped;
      });
      setProperties(mappedProperties);
      setCurrentPage(1); // Reset para primeira página ao recarregar
    } catch (error: any) {
      console.error('Erro ao carregar propriedades:', error);
      alert('Erro ao carregar propriedades: ' + (error.message || 'Erro desconhecido'));
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientAPI.get('/clients');
      setClients(response.data.clients || []);
    } catch (error) {
      }
  };

  const createClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await clientAPI.post('/clients', newClient);
      const createdClient = response.data.client;
      
      // Adicionar cliente à lista
      setClients([...clients, createdClient]);
      
      // Selecionar o cliente recém-criado no formulário de propriedade
      setNewProperty({ ...newProperty, client_id: createdClient.id });
      
      // Fechar modal e limpar formulário
      setShowClientModal(false);
      setNewClient({ name: '', phone: '', document: '' });
      
      alert(t('success'));
    } catch (error: any) {
      setError(t('error') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  // Função para mapear tipo do frontend para tipo do backend
  const mapPropertyTypeToBackend = (frontendType: string): 'residential' | 'commercial' | 'other' => {
    const typeMap: Record<string, 'residential' | 'commercial' | 'other'> = {
      'house': 'residential',
      'apartment': 'residential',
      'condo': 'residential',
      'commercial': 'commercial',
      'other': 'other'
    };
    return typeMap[frontendType] || 'residential';
  };

  const createProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!newProperty.client_id) {
      setError(t('error'));
      return;
    }
    
    setIsLoading(true);
    try {
      // Mapear tipo do frontend para backend
      const backendPropertyType = mapPropertyTypeToBackend(newProperty.type);
      // Validar price
      if (!newProperty.price || newProperty.price.trim() === '') {
        setError(t('validation.priceRequired'));
        setIsLoading(false);
        return;
      }
      
      const priceValue = parseFloat(newProperty.price);
      if (isNaN(priceValue) || priceValue <= 0) {
        setError(t('validation.priceRequired'));
        setIsLoading(false);
        return;
      }
      
      // Usar API real para criar propriedade (retorna UUID)
      const response = await createPropertyAPI({
        client_id: newProperty.client_id,
        name: newProperty.name,
        address: newProperty.address,
        zip_code: newProperty.zip_code || undefined,
        city: newProperty.city || undefined,
        state: newProperty.state || undefined,
        property_type: backendPropertyType,
        price: priceValue, // Garantir que sempre seja um número válido
        special_instructions: undefined
      });
      
      const createdProperty = response.property;
      if (!createdProperty) {
        throw new Error('Propriedade não retornada pela API');
      }
      
      // Recarregar propriedades da API
      await loadProperties();
      
      setShowPropertyForm(false);
      setNewProperty({ client_id: '', name: '', address: '', zip_code: '', city: '', state: '', type: 'house', price: '' });
      setError('');
      setIsLoading(false);
      alert(t('success') || 'Propriedade criada com sucesso!');
      if (onClientCleared) {
        onClientCleared();
      }
    } catch (error: any) {
      console.error('Erro ao criar propriedade:', error);
      setError(error.response?.data?.error || error.message || t('error') || 'Erro ao criar propriedade');
      setIsLoading(false);
    }
  };

  const editProperty = (property: Property) => {
    setEditingProperty(property);
    // Mapear property_type do backend para type do frontend se necessário
    const propertyType = property.type || (property as any).property_type || 'house';
    const mappedType = propertyType === 'residential' ? 'house' : 
                      propertyType === 'commercial' ? 'commercial' : 
                      propertyType === 'other' ? 'other' : propertyType;
    
    const propertyData = {
      client_id: property.client_id,
      name: property.name,
      address: property.address,
      zip_code: property.zip_code || '',
      city: property.city || '',
      state: property.state || '',
      type: mappedType,
      price: property.price ? property.price.toString() : '0'
    };
    
    setNewProperty(propertyData);
    setShowPropertyForm(true);
  };

  const updateProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProperty) return;

    setIsLoading(true);
    try {
      // Validar price
      const priceValue = parseFloat(newProperty.price) || 0;
      if (priceValue <= 0) {
        setError(t('validation.priceRequired'));
        setIsLoading(false);
        return;
      }
      
      // Preparar dados para atualização
      const updateData = {
        name: newProperty.name,
        address: newProperty.address,
        zip_code: newProperty.zip_code || undefined,
        city: newProperty.city || undefined,
        state: newProperty.state || undefined,
        property_type: mapPropertyTypeToBackend(newProperty.type),
        price: priceValue
      };
      
      // Usar API real para atualizar propriedade
      const response = await updatePropertyAPI(editingProperty.id, updateData);
      
      const updatedProperty = response.property;
      if (!updatedProperty) {
        throw new Error('Propriedade não retornada pela API');
      }
      
      // Recarregar propriedades da API
      await loadProperties();
      
      setShowPropertyForm(false);
      setEditingProperty(null);
      setNewProperty({ client_id: '', name: '', address: '', zip_code: '', city: '', state: '', type: 'house', price: '' });
      setError('');
      setIsLoading(false);
      alert(t('success') || 'Propriedade atualizada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar propriedade:', error);
      setError(error.response?.data?.error || error.message || t('error') || 'Erro ao atualizar propriedade');
      setIsLoading(false);
    }
  };

  const deleteProperty = async (propertyId: string) => {
    if (!confirm(t('confirmDelete') || 'Tem certeza que deseja deletar esta propriedade?')) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Substituir por chamada real à API quando disponível
      setTimeout(() => {
        setProperties(properties.filter(p => p.id !== propertyId));
        setIsLoading(false);
        alert(t('success'));
      }, 500);
    } catch (error) {
      setError(t('error'));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      setNewProperty(prev => ({
        ...prev,
        client_id: selectedClient.id
      }));
      setShowPropertyForm(true);
    }
  }, [selectedClient]);

  // Calcular propriedades paginadas usando useMemo
  const paginatedProperties = useMemo(() => {
    if (!Array.isArray(properties)) {
      return [];
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return properties.slice(startIndex, endIndex);
  }, [properties, currentPage, itemsPerPage]);

  const getPropertyTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      house: t('house'),
      apartment: t('apartment'),
      condo: t('condo'),
      commercial: t('commercial')
    };
    return types[type] || type;
  };

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('propertyManagement')}</h1>
        <p style={{ color: '#666', margin: 0 }}>{t('propertySubtitle')}</p>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>{t('propertyList')}</h2>
          <button
            onClick={() => setShowPropertyForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Plus size={18} />
            {t('addProperty')}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            {t('loadingProperties')}
          </div>
        ) : properties.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Home size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p>{t('noPropertiesFound')}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {paginatedProperties.map(property => (
              <div
                key={property.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Home size={20} style={{ color: '#6366f1' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{property.name}</h3>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                  <User size={16} />
                  <span>{property.client_name || 'N/A'}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                  <MapPin size={16} />
                  <span>{property.address}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {getPropertyTypeLabel(property.type)}
                  </span>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600', color: '#10b981' }}>
                    <DollarSign size={16} />
                    <span>${(property.price || 0).toLocaleString()}</span>
                  </div>
                </div>
                
                {/* Botões de Ação */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPropertyForAnimals({ id: property.id, name: property.name });
                      setShowAnimalModal(true);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                  >
                    <Shield size={14} />
                    Animais
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPropertyForInstructions({ id: property.id, name: property.name });
                      setShowInstructionsModal(true);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                  >
                    <AlertCircle size={14} />{t('properties.instructions')}</button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      editProperty(property);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                  >
                    <Edit size={14} />{t('actions.edit')}</button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteProperty(property.id);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                  >
                    <Trash2 size={14} />{t('actions.delete')}</button>
                </div>
              </div>
            ))}
            </div>
            {properties.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(properties.length / itemsPerPage)}
                totalItems={properties.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onItemsPerPageChange={(newItemsPerPage) => {
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Modal de Cadastro de Propriedade */}
      {showPropertyForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>
              {editingProperty ? 'Editar Propriedade' : t('addProperty')}
            </h2>
            
            <form onSubmit={editingProperty ? updateProperty : createProperty}>
              {/* Seleção de Cliente */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  {t('client')} *
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    required
                    value={newProperty.client_id}
                    onChange={(e) => setNewProperty({ ...newProperty, client_id: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowClientModal(true)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + New Client
                  </button>
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                  💡 Client not found? Click "New Client" to register
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  {t('propertyName')} *
                </label>
                <input
                  type="text"
                  required
                  value={newProperty.name}
                  onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })}
                  placeholder="e.g., Beach House"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* CEP/ZIP Code Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  CEP / ZIP Code
                  {detectedCountry && (
                    <span style={{ 
                      marginLeft: '8px', 
                      padding: '2px 8px', 
                      backgroundColor: detectedCountry === 'BR' ? '#10b981' : '#3b82f6',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {detectedCountry === 'BR' ? '🇧🇷 Brasil' : '🇺🇸 USA'}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={newProperty.zip_code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setNewProperty({ ...newProperty, zip_code: value });
                    
                    // Buscar automaticamente quando atingir tamanho válido
                    if (value.length === 5 || value.length === 8) {
                      fetchAddressByCep(value);
                    }
                    
                    // Limpar país detectado se apagar
                    if (value.length < 5) {
                      setDetectedCountry(null);
                    }
                  }}
                  placeholder="00000-000 (BR) or 12345 (US)"
                  maxLength={9}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {loadingCep && (
                  <small style={{ color: '#6366f1', display: 'block', marginTop: '4px' }}>
                    🔍 {detectedCountry === 'BR' ? 'Buscando CEP...' : 'Searching ZIP Code...'}
                  </small>
                )}
                {!loadingCep && !detectedCountry && (
                  <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>{t('properties.cepHint')}</small>
                )}
              </div>

              {/* City and State Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={newProperty.city}
                    onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })}
                    placeholder="São Paulo"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Estado (UF)
                  </label>
                  <input
                    type="text"
                    value={newProperty.state}
                    onChange={(e) => setNewProperty({ ...newProperty, state: e.target.value.toUpperCase() })}
                    placeholder="SP"
                    maxLength={2}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  {t('address')} *
                </label>
                <input
                  type="text"
                  required
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
                  placeholder={detectedCountry === 'US' ? 'e.g., 123 Main St, Apt 4B' : 'e.g., Rua das Flores, 123 - Centro'}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  💡 {detectedCountry === 'US' 
                    ? 'ZIP Code auto-fills city and state' 
                    : 'O CEP/ZIP Code preenche automaticamente cidade e estado'}
                </small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  {t('propertyType')} *
                </label>
                <select
                  required
                  value={newProperty.type}
                  onChange={(e) => setNewProperty({ ...newProperty, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="condo">Condo</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  {t('propertyPrice')} *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={newProperty.price}
                  onChange={(e) => setNewProperty({ ...newProperty, price: e.target.value })}
                  placeholder="e.g., 2500"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPropertyForm(false);
                    setEditingProperty(null);
                    setNewProperty({ client_id: '', name: '', address: '', zip_code: '', city: '', state: '', type: 'house', price: '' });
                    setError('');
                    if (onClientCleared) {
                      onClientCleared();
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: editingProperty ? '#10b981' : '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading ? (editingProperty ? 'Atualizando...' : 'Criando...') : (editingProperty ? 'Atualizar' : 'Criar Propriedade')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Cadastro de Cliente */}
      {showClientModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Register New Client</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '-8px' }}>
              After registering, the client will be automatically selected
            </p>
            
            <form onSubmit={createClient}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="e.g., John Doe"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <PhoneInput
                label="Phone"
                value={newClient.phone}
                onChange={(value) => setNewClient({ ...newClient, phone: value })}
                required
              />

              <DocumentInput
                label="Document ID"
                value={newClient.document}
                onChange={(value) => setNewClient({ ...newClient, document: value })}
                placeholder="Enter document ID"
                required
              />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowClientModal(false);
                    setNewClient({ name: '', phone: '', document: '' });
                    setError('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Register Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Gestão de Animais */}
      {selectedPropertyForAnimals && (
        <AnimalManagementModal
          isOpen={showAnimalModal}
          onClose={() => {
            setShowAnimalModal(false);
            setSelectedPropertyForAnimals(null);
          }}
          propertyId={selectedPropertyForAnimals.id}
          propertyName={selectedPropertyForAnimals.name}
        />
      )}

      {selectedPropertyForInstructions && (
        <SpecialInstructionsModal
          isOpen={showInstructionsModal}
          onClose={() => {
            setShowInstructionsModal(false);
            setSelectedPropertyForInstructions(null);
          }}
          propertyId={selectedPropertyForInstructions.id}
          propertyName={selectedPropertyForInstructions.name}
        />
      )}
    </div>
  );
};

export default Propriedades;

